/**
 * Rewrite persisted connector zone ids when discovery folds RAAT faces away.
 */

import { resolveZoneIdViaAliases } from '../handlers/zoneDeduplication';
import { LOCAL_ZONE_STORAGE_ID, isLocalZoneStorageId } from '../utils/localZoneStorage';

const CONNECTOR_ZONE_STORAGE_KEY = 'maestro_connector_active_zone';
const SAVED_ZONE_META_KEY = 'maestro_saved_zone_meta';

type SavedZoneMeta = {
  id: string;
  name: string;
  type?: string;
};

export function readPersistedConnectorZoneId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    return localStorage.getItem(CONNECTOR_ZONE_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function writePersistedConnectorZoneId(zoneId: string): void {
  if (typeof localStorage === 'undefined' || !zoneId) return;
  const normalized = isLocalZoneStorageId(zoneId) ? LOCAL_ZONE_STORAGE_ID : zoneId;
  try {
    localStorage.setItem(CONNECTOR_ZONE_STORAGE_KEY, normalized);
  } catch { /* quota */ }
}

function readSavedZoneMeta(): SavedZoneMeta | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(SAVED_ZONE_META_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SavedZoneMeta;
    if (!parsed?.id) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeSavedZoneMeta(meta: SavedZoneMeta): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(SAVED_ZONE_META_KEY, JSON.stringify(meta));
  } catch { /* quota */ }
}

/**
 * When a saved id maps via folded_to / renderer_id_aliases, rewrite storage
 * so a pinned raat-* id cannot outlive folding.
 */
export function remapPersistedConnectorZoneIfFolded(
  aliases: Record<string, string>,
  zones: Array<{ id: string; name?: string; type?: string; host?: string }>,
): string | null {
  const saved = readPersistedConnectorZoneId();
  if (!saved || isLocalZoneStorageId(saved)) return null;

  const resolved = resolveZoneIdViaAliases(saved, aliases);
  if (resolved === saved) return null;

  const target = zones.find((z) => z.id === resolved);
  writePersistedConnectorZoneId(resolved);

  const meta = readSavedZoneMeta();
  if (meta?.id === saved || meta?.id === resolved) {
    writeSavedZoneMeta({
      id: resolved,
      name: target?.name || meta?.name || resolved,
      type: target?.type || meta?.type,
    });
  }

  console.log(`[zone-fold-remap] persisted zone ${saved} → ${resolved}`);
  return resolved;
}
